import { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Volume2, VolumeX, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available voices for story reading
export const VOICE_OPTIONS = {
  fable: { name: "Fable", desc: "Expressive & Storytelling", icon: "📖" },
  nova: { name: "Nova", desc: "Energetic & Upbeat", icon: "✨" },
  shimmer: { name: "Shimmer", desc: "Bright & Cheerful", icon: "🌟" },
  echo: { name: "Echo", desc: "Smooth & Calm", icon: "🌊" },
  onyx: { name: "Onyx", desc: "Deep & Authoritative", icon: "🎭" },
  coral: { name: "Coral", desc: "Warm & Friendly", icon: "🌸" }
};

export const SETTINGS_VOICE_OPTIONS = {
  nova: { name: "Nova", desc: "Warm narrator" },
  onyx: { name: "Onyx", desc: "Deep storyteller" }
};

export const DEFAULT_TTS_VOICE = 'nova';

const getSavedTtsVoice = () => {
  try {
    const savedVoice = localStorage.getItem('storyteller_voice');
    return SETTINGS_VOICE_OPTIONS[savedVoice] ? savedVoice : DEFAULT_TTS_VOICE;
  } catch {
    return DEFAULT_TTS_VOICE;
  }
};

/**
 * Hook for voice-to-text and text-to-voice functionality
 * Uses OpenAI TTS for natural, human-like story reading
 */
export function useVoiceFeatures() {
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(getSavedTtsVoice);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const shouldRestartRef = useRef(false);
  const onResultCallbackRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const whisperStreamRef = useRef(null);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioQueueRef = useRef([]);
  const currentSegmentRef = useRef(0);
  const streamingCompleteRef = useRef(false);
  const waitingForNextChunkRef = useRef(false);
  const deepgramWsRef = useRef(null);
  const deepgramStreamRef = useRef(null);
  const deepgramRecorderRef = useRef(null);
  const deepgramAudioCtxRef = useRef(null);
  const deepgramProcessorRef = useRef(null);
  const deepgramWorkletLoadedRef = useRef(false);
  const whisperAudioCtxRef = useRef(null);
  const whisperSourceRef = useRef(null);
  const pendingFinalTranscriptRef = useRef('');
  const deepgramReconnectAttemptsRef = useRef(0);
  const deepgramRunRef = useRef(0);
  const deepgramCaptureModeRef = useRef('none');
  const speechCallbacksRef = useRef(null);
  const speechOptionsRef = useRef({});
  const toastGuardRef = useRef({});
  const deepgramTokenCacheRef = useRef({ token: null, expiresAt: 0 });
  const [isRecordedMode, setIsRecordedMode] = useState(false);

  const DEEPGRAM_MAX_RECONNECTS = 3;
  const DEEPGRAM_AUDIO_CONSTRAINTS = {
    echoCancellation: true,
    noiseSuppression: true,
    sampleRate: 44100
  };

  const hasActiveUserGesture = () => {
    return navigator.userActivation?.isActive ?? true;
  };

  const isAndroid = () => /Android/i.test(navigator.userAgent);
  const getDeepgramEndpointingMs = () => (isAndroid() ? 800 : 500);

  const notify = (key, message, type = 'error', ttl = 5000) => {
    const now = Date.now();
    if (toastGuardRef.current[key] && now - toastGuardRef.current[key] < ttl) return;
    toastGuardRef.current[key] = now;
    const notifyFn = toast[type] || toast;
    notifyFn(message);
  };

  const getMicErrorMessage = (error) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      return 'Microphone is not supported in this browser';
    }
    if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
      return 'Microphone access denied';
    }
    if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
      return 'No microphone was found';
    }
    if (error?.name === 'NotReadableError' || error?.name === 'TrackStartError') {
      return 'Microphone is already in use';
    }
    return 'Could not start the microphone';
  };

  const requestDeepgramMicrophoneAccess = async () => {
    if (deepgramStreamRef.current?.active) return true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: DEEPGRAM_AUDIO_CONSTRAINTS
      });
      deepgramStreamRef.current = stream;
      return true;
    } catch (err) {
      console.error('Mic access failed:', err);
      notify('mic-access-denied', getMicErrorMessage(err));
      return false;
    }
  };

  const notifySpeechStarted = () => {
    const callbacks = speechCallbacksRef.current;
    if (!callbacks || callbacks.started) return;
    callbacks.started = true;
    callbacks.onStart?.();
  };

  const finishSpeechPlayback = (error = null) => {
    const callbacks = speechCallbacksRef.current;
    speechCallbacksRef.current = null;
    setIsSpeaking(false);
    if (error) {
      callbacks?.onError?.(error);
    } else {
      callbacks?.onEnd?.();
    }
  };

  const speakWithBrowserVoice = (text, options = {}) => {
    if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) {
      finishSpeechPlayback(new Error('Browser speech synthesis is not supported.'));
      return false;
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = options.lang || 'en-US';
    utterance.rate = options.rate || 0.95;
    utterance.pitch = options.pitch || 1;
    utterance.volume = 1;

    utterance.onstart = () => {
      setIsLoading(false);
      setIsSpeaking(true);
      notifySpeechStarted();
    };
    utterance.onend = () => {
      finishSpeechPlayback();
    };
    utterance.onerror = (event) => {
      finishSpeechPlayback(event.error || new Error('Browser speech failed.'));
    };

    setIsLoading(true);
    window.speechSynthesis.speak(utterance);
    return true;
  };

  useEffect(() => {
    // Check if browser supports speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSupported(!!SpeechRecognition);

    // IMPORTANT: Try to unlock audio on ANY user interaction with the page
    // This maximizes the chance of having audio ready when "Read" is clicked
    const earlyUnlock = () => {
      if (!audioUnlockedRef.current) {
        try {
          if (!hasActiveUserGesture()) return;
          if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
          }
          const silentAudio = silentAudioRef.current || new Audio("data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYAAAAAAAAAAAAAAAAAAAAA");
          silentAudioRef.current = silentAudio;
          audioRef.current = silentAudio;
          silentAudio.volume = 0.001;
          silentAudio.loop = true;
          if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
          }
          // Play silent buffer
          const buffer = audioContextRef.current.createBuffer(1, 1, 22050);
          const source = audioContextRef.current.createBufferSource();
          source.buffer = buffer;
          source.connect(audioContextRef.current.destination);
          source.start(0);
          silentAudio.play().catch(() => {});
          audioUnlockedRef.current = true;
        } catch (e) {
          // Ignore - will try again on Read click
        }
      }
    };

    // Listen for any user interaction to pre-unlock audio
    document.addEventListener('click', earlyUnlock, { once: true });
    document.addEventListener('touchstart', earlyUnlock, { once: true });
    document.addEventListener('keydown', earlyUnlock, { once: true });

    // Cleanup on unmount
    return () => {
      document.removeEventListener('click', earlyUnlock);
      document.removeEventListener('touchstart', earlyUnlock);
      document.removeEventListener('keydown', earlyUnlock);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      // Clean up microphone stream on unmount
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
        micStreamRef.current = null;
      }
      // Clean up animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Clean up audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      // Clean up Deepgram resources
      try {
        deepgramRecorderRef.current?.stop();
      } catch {
        // Recorder may already be inactive during teardown.
      }
      deepgramProcessorRef.current?.disconnect();
      if (deepgramWsRef.current) {
        deepgramWsRef.current.onclose = null;
        deepgramWsRef.current.close();
      }
      deepgramStreamRef.current?.getTracks().forEach(t => t.stop());
      deepgramAudioCtxRef.current?.close();
      whisperStreamRef.current?.getTracks().forEach(t => t.stop());
      whisperSourceRef.current?.disconnect();
      whisperAudioCtxRef.current?.close();
    };
  }, []);

  useEffect(() => {
    const syncSavedVoice = () => {
      setSelectedVoice(getSavedTtsVoice());
    };

    window.addEventListener('storage', syncSavedVoice);
    window.addEventListener('storyteller-voice-change', syncSavedVoice);

    return () => {
      window.removeEventListener('storage', syncSavedVoice);
      window.removeEventListener('storyteller-voice-change', syncSavedVoice);
    };
  }, []);

  // Save voice preference when changed
  useEffect(() => {
    localStorage.setItem('storyteller_voice', selectedVoice);
  }, [selectedVoice]);

  // AudioContext for unlocking browser audio (already declared above)
  const audioUnlockedRef = useRef(false);
  const silentAudioRef = useRef(null);  // Pre-created silent audio element

  // Unlock audio on first user interaction - creates audio element IMMEDIATELY
  const unlockAudio = () => {
    if (audioUnlockedRef.current) return true;
    if (!hasActiveUserGesture()) return false;

    console.log('[ConversationVoice] unlockAudio: starting, audioContextRef state before:', audioContextRef.current?.state ?? 'none');
    try {
      // Create AudioContext if not exists
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      // Resume if suspended (must be synchronous in click handler)
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      
      // Play silent buffer to unlock - synchronous
      const buffer = audioContextRef.current.createBuffer(1, 1, 22050);
      const source = audioContextRef.current.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContextRef.current.destination);
      source.start(0);
      
      // Create and unlock one persistent HTML audio element. Reusing this
      // element is more reliable than creating a fresh Audio() after async work.
      const silentAudio = silentAudioRef.current || new Audio("data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYAAAAAAAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYAAAAAAAAAAAAAAAAAAAAA");
      silentAudioRef.current = silentAudio;
      audioRef.current = silentAudio;
      silentAudio.volume = 0.001;  // Nearly silent
      silentAudio.loop = true;
      silentAudio.play().catch(() => {
        // Ignore - this is expected on some browsers
      });
      
      audioUnlockedRef.current = true;
      return true;
    } catch (err) {
      console.error('Failed to unlock audio:', err);
      return false;
    }
  };

  // Play the next audio segment in queue
  const playNextSegment = async () => {
    if (currentSegmentRef.current >= audioQueueRef.current.length) {
      if (streamingCompleteRef.current) {
        // Streaming is done and queue is exhausted — truly finished
        audioQueueRef.current = [];
        currentSegmentRef.current = 0;
        streamingCompleteRef.current = false;
        waitingForNextChunkRef.current = false;
        finishSpeechPlayback();
      } else {
        // More chunks are still coming — pause playback until next chunk arrives
        waitingForNextChunkRef.current = true;
      }
      return;
    }
    waitingForNextChunkRef.current = false;

    const segment = audioQueueRef.current[currentSegmentRef.current];
    
    // Validate segment data
    if (!segment || !segment.audio_base64) {
      console.error('Invalid audio segment:', currentSegmentRef.current);
      currentSegmentRef.current++;
      playNextSegment();
      return;
    }
    
    try {
      // Ensure AudioContext is running (needed for iOS)
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      // Reuse the pre-unlocked audio element (critical for iOS autoplay policy).
      // Creating new Audio() after async work breaks iOS's gesture chain.
      const audio = silentAudioRef.current;
      if (!audio) {
        throw new Error('Audio is not unlocked yet');
      }
      audioRef.current = audio;
      audio.loop = false;
      audio.playbackRate = playbackSpeed;
      audio.volume = 1.0;

      const segmentBlob = base64ToBlob(segment.audio_base64, segment.mime_type || 'audio/mpeg');
      const segmentUrl = URL.createObjectURL(segmentBlob);
      audio.src = segmentUrl;
      audio.load();

      audio.onended = () => {
        URL.revokeObjectURL(segmentUrl);
        currentSegmentRef.current++;
        playNextSegment();
      };

      audio.onerror = (e) => {
        const mediaErr = e.target?.error;
        console.error('Audio error on segment', currentSegmentRef.current, mediaErr?.code, mediaErr?.message);
        URL.revokeObjectURL(segmentUrl);
        currentSegmentRef.current++;
        if (currentSegmentRef.current < audioQueueRef.current.length) {
          playNextSegment();
        } else {
          finishSpeechPlayback(new Error('Audio playback error'));
          notify('audio-segment-error', 'Audio playback error');
        }
      };

      await audio.play();
      notifySpeechStarted();
    } catch (err) {
      console.error('[TTS] playNextSegment audio.play() failed:', err.name, err.message);

      if (err.name === 'NotAllowedError') {
        // iOS blocked play — element is no longer considered unlocked; reset so
        // the next user-gesture call to primeAudio() re-unlocks it.
        audioUnlockedRef.current = false;
        setIsLoading(false);
        finishSpeechPlayback(err);
        notify('audio-tap-again', 'Tap Read again to start audio');
      } else {
        currentSegmentRef.current++;
        if (currentSegmentRef.current < audioQueueRef.current.length) {
          playNextSegment();
        } else {
          finishSpeechPlayback(err);
          notify('audio-play-failed', 'Failed to play audio');
        }
      }
    }
  };

  // Detect if running on iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Main speak function
  const speak = async (text, options = {}) => {
    // Enhanced validation with detailed logging
    if (!text) {
      console.error('TTS Error: text is null or undefined');
      notify('tts-no-text', 'No text to read');
      return;
    }
    
    if (typeof text !== 'string') {
      console.error('TTS Error: text is not a string, type:', typeof text, 'value:', text);
      notify('tts-invalid-text', 'Invalid text format');
      return;
    }
    
    const trimmedText = text.trim();
    if (trimmedText.length === 0) {
      notify('tts-no-text', 'No text to read');
      return;
    }

    // Stop any current playback
    stopSpeaking();
    speechOptionsRef.current = options;
    speechCallbacksRef.current = {
      onStart: options.onStart,
      onEnd: options.onEnd,
      onError: options.onError,
      started: false
    };

    if (options.preferBrowserTTS) {
      speakWithBrowserVoice(trimmedText, options);
      return;
    }

    // Browser audio playback requires a user gesture. Auto interview mode can
    // still continue by falling through to the mic when speech is blocked.
    if (!unlockAudio()) {
      const error = new Error('Audio playback requires a user interaction first.');
      if (!options.suppressAutoplayError) {
        notify('tts-unlock-needed', 'Tap the read button once to enable audio.');
      }
      finishSpeechPlayback(error);
      return;
    }

    setIsLoading(true);

    const voice = options.voice || selectedVoice;
    const speed = options.speed || playbackSpeed;

    try {
      if (options.forceStream || text.length > 500) {
        // Streaming: starts playing as first chunk arrives (~0.5s).
        // forceStream is used by conversation mode so short questions play fast too.
        await speakWithStreaming(text, voice, speed, options.forceStream ? 'wav' : 'mp3');
      } else {
        // Short text (questions, prompts): single fast request
        await speakWithChunked(text, voice, speed);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('TTS error:', error);
      finishSpeechPlayback(error);
      notify('tts-speech-failed', `Speech failed: ${error.message || 'Unknown error'}`);
    }
  };

  // Non-streaming TTS for iOS - loads all audio as single file, then plays
  // CRITICAL: Different approach - fetch entire audio first, then play with standard Audio element
  const speakWithChunked = async (text, voice, speed) => {
    try {
      // iOS Strategy: Fetch ENTIRE audio file as one blob, then play
      // This avoids chunking issues and autoplay policy violations
      
      // Truncate very long text to avoid timeouts (4096 char limit per OpenAI)
      const truncatedText = text.length > 4096 ? text.substring(0, 4096) + '...' : text;
      
      // Reuse the element unlocked by the user's Voice mode / Read click.
      const audio = silentAudioRef.current;
      if (!audio) {
        throw new Error('Audio is not unlocked yet');
      }
      audioRef.current = audio;
      audio.loop = false;
      audio.playbackRate = speed;
      audio.volume = 1.0;
      
      // Start "loading" by setting a data URL (keeps audio context alive)
      // This maintains the user gesture chain
      audio.src = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYAAAAAAAAAAAAAAAAAAAAA";
      
      // Get auth token from localStorage
      const authToken = localStorage.getItem('auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      // Fetch ENTIRE audio file as base64 from the single /generate endpoint
      const response = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ 
          text: truncatedText, 
          voice, 
          speed, 
          model: 'tts-1'  // Use faster model for better UX
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('TTS API Error:', {
          status: response.status,
          detail: errorData.detail,
          requestBody: { textLength: truncatedText.length, voice, speed }
        });
        
        // User-friendly error messages based on status code
        let errorMessage = errorData.detail || 'Speech generation failed';
        
        if (response.status === 400) {
          // Bad request - likely validation error
          if (errorMessage.includes('empty')) {
            errorMessage = 'Text is empty. Please add content to read.';
          } else if (errorMessage.includes('too long')) {
            errorMessage = 'Text is too long. Maximum 4096 characters.';
          } else if (errorMessage.includes('Invalid voice')) {
            errorMessage = 'Selected voice is not available. Please try a different voice.';
          }
        } else if (response.status === 500) {
          errorMessage = 'Text-to-speech service is temporarily unavailable. Please try again later.';
        } else if (response.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (!data.audio_base64) {
        throw new Error('No audio data received from server');
      }

      // Convert base64 to blob URL
      const audioBlob = base64ToBlob(data.audio_base64, 'audio/mp3');
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Update the SAME audio element's src (maintains user gesture context)
      audio.src = audioUrl;
      
      // Setup event handlers
      audio.onended = () => {
        finishSpeechPlayback();
        audio.onerror = null;  // prevent spurious error from load() with no src
        audio.removeAttribute('src');
        audio.load();
        URL.revokeObjectURL(audioUrl);  // Clean up blob URL
      };

      audio.onerror = (e) => {
        const mediaErr = e.target?.error;
        console.error('Audio playback error:', mediaErr?.code, mediaErr?.message, e);
        setIsLoading(false);
        finishSpeechPlayback(e);
        audio.onerror = null;
        audio.removeAttribute('src');
        audio.load();
        URL.revokeObjectURL(audioUrl);
        notify('audio-playback-failed', 'Audio playback failed. Please try again.');
      };
      
      setIsLoading(false);
      setIsSpeaking(true);
      
      // Play the audio - this should work because we maintained the user gesture chain
      try {
        // CRITICAL: Use play() promise correctly for iOS
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
        notifySpeechStarted();
      } catch (playError) {
        console.error('[TTS] speakWithChunked audio.play() failed:', playError.name, playError.message);
        setIsLoading(false);
        audio.removeAttribute('src');
        audio.load();
        URL.revokeObjectURL(audioUrl);
        finishSpeechPlayback(playError);

        if (playError.name === 'NotAllowedError') {
          // iOS blocked play — reset unlock flag so primeAudio() can re-unlock on next tap.
          audioUnlockedRef.current = false;
          if (!speechOptionsRef.current.suppressAutoplayError) {
            notify('tts-tap-once-more', 'Tap Read once more to start audio');
          }
        } else if (playError.name === 'NotSupportedError') {
          notify('tts-format-unsupported', 'Audio format not supported. Try refreshing the page.');
        } else {
          notify('audio-play-failed', 'Failed to play audio. Please try again.');
        }
      }
      
    } catch (error) {
      setIsLoading(false);
      finishSpeechPlayback(error);
      console.error('iOS TTS error:', error);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Speech generation failed';
      notify('tts-generation-failed', errorMessage);
    }
  };
  
  // Helper function to convert base64 to Blob
  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  // Streaming TTS — starts playing as soon as the first chunk arrives.
  // Backend generates all chunks in parallel so total wait time is minimal.
  const speakWithStreaming = async (text, voice, speed, responseFormat = 'mp3') => {
    // Reset streaming state
    streamingCompleteRef.current = false;
    waitingForNextChunkRef.current = false;

    try {
      // Get auth token from localStorage
      const authToken = localStorage.getItem('auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      // Use fetch with POST for SSE (EventSource doesn't support POST)
      const requestedAt = performance.now();
      const response = await fetch(`${API}/tts/generate-stream`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ text, voice, speed, model: 'tts-1', response_format: responseFormat })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Stream response error:', response.status, errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Streaming not supported by browser');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let firstChunkReceived = false;
      let chunksReceived = 0;
      let streamMimeType = responseFormat === 'wav' ? 'audio/wav' : 'audio/mpeg';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete SSE messages
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete message in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'metadata') {
                streamMimeType = data.mime_type || streamMimeType;
              } else if (data.type === 'chunk') {
                chunksReceived++;
                const mimeType = data.mime_type || streamMimeType;
                audioQueueRef.current.push({
                  index: data.index,
                  audio_base64: data.audio_base64,
                  mime_type: mimeType
                });

                if (!firstChunkReceived) {
                  // Start playing as soon as the first chunk arrives
                  firstChunkReceived = true;
                  console.log('[ConversationTiming] tts_first_chunk_ms', Math.round(performance.now() - requestedAt));
                  currentSegmentRef.current = 0;
                  setIsSpeaking(true);
                  setIsLoading(false);
                  playNextSegment();
                } else if (waitingForNextChunkRef.current) {
                  // Playback was waiting for this chunk — resume
                  playNextSegment();
                }
              } else if (data.type === 'complete') {
                streamingCompleteRef.current = true;
                // If playback is waiting and nothing is in the queue, we're done
                if (waitingForNextChunkRef.current) {
                  audioQueueRef.current = [];
                  currentSegmentRef.current = 0;
                  waitingForNextChunkRef.current = false;
                  finishSpeechPlayback();
                }
              } else if (data.type === 'error') {
                console.error('Server chunk error:', data.message);
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
      
      // If no chunks were received, something went wrong
      if (chunksReceived === 0) {
        throw new Error('No audio data received from server');
      }
    } catch (error) {
      console.error('Streaming TTS error:', error);
      setIsLoading(false);
      finishSpeechPlayback(error);
      
      if (error.message?.includes('Server error')) {
        notify('tts-stream-server', 'Server error while streaming. Please try again.');
      } else if (error.message?.includes('Streaming not supported')) {
        notify('tts-stream-unsupported', 'Your browser does not support audio streaming.');
      } else {
        notify('tts-stream-failed', `Streaming failed: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const stopSpeaking = () => {
    speechCallbacksRef.current = null;
    speechOptionsRef.current = {};
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    audioQueueRef.current = [];
    currentSegmentRef.current = 0;
    streamingCompleteRef.current = false;
    waitingForNextChunkRef.current = false;
    setIsSpeaking(false);
    setIsLoading(false);
  };

  const startWhisperListening = async (onResult, opts = {}) => {
    const autoStop = opts.autoStop === true;
    const recordedStartedAt = performance.now();
    try {
      console.log('[ConversationTiming] recorded_transcription_start', { autoStop });
      setIsRecordedMode(true);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      whisperStreamRef.current = stream;
      audioChunksRef.current = [];
      shouldRestartRef.current = true;
      setInterimTranscript('');

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      const existingWhisperCtx = whisperAudioCtxRef.current;
      const audioCtx = (existingWhisperCtx && existingWhisperCtx.state !== 'closed')
        ? existingWhisperCtx
        : new (window.AudioContext || window.webkitAudioContext)();
      whisperAudioCtxRef.current = audioCtx;
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }
      console.log('[ConversationVoice] Whisper recorder AudioContext state:', audioCtx.state);
      const source = audioCtx.createMediaStreamSource(stream);
      whisperSourceRef.current = source;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let lastLevelUpdate = 0;

      // Silence auto-stop state (only used when autoStop=true)
      const WHISPER_SILENCE_THRESHOLD = 8;   // 0-100 scale; below this = silent
      // 2s, not 4s. This path has no live transcript, so every millisecond here is
      // dead air on top of the upload and transcription that still have to happen.
      // The `hasSpoken` gate below already prevents a premature stop.
      const WHISPER_SILENCE_DURATION = 2000; // ms of continuous silence before stop
      const WHISPER_GRACE_MS = 1500;         // don't detect silence in the first 1.5s
      const recordingStartedAt = Date.now();
      let silenceStartedAt = null;
      let hasSpoken = false;                  // only start timer after user has spoken
      let maxRecordingTimer = null;

      const updateLevel = () => {
        if (!shouldRestartRef.current || analyserRef.current !== analyser) return;
        const now = Date.now();
        if (now - lastLevelUpdate > 80) {
          analyser.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
          const level = Math.min(100, (avg / 255) * 180);
          setAudioLevel(level);
          lastLevelUpdate = now;

          // Silence detection: only in conversation-mode Whisper fallback
          if (autoStop && (now - recordingStartedAt) >= WHISPER_GRACE_MS) {
            if (level >= WHISPER_SILENCE_THRESHOLD) {
              hasSpoken = true;
              silenceStartedAt = null;
            } else if (hasSpoken) {
              if (silenceStartedAt === null) {
                silenceStartedAt = now;
              } else if (now - silenceStartedAt >= WHISPER_SILENCE_DURATION) {
                if (mediaRecorderRef.current?.state === 'recording') {
                  console.log('[ConversationVoice] Whisper silence detected, auto-stopping');
                  silenceStartedAt = null;
                  mediaRecorderRef.current.stop();
                  return; // don't queue another RAF — onstop will clean up
                }
              }
            }
          }
        }
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();

      mediaRecorder.onstop = async () => {
        if (autoStop) console.log('[ConversationVoice] Whisper auto-stop complete, transcription pending');
        console.log('[ConversationTiming] recorded_media_stop', {
          elapsedMs: Math.round(performance.now() - recordedStartedAt)
        });
        setIsListening(false);
        setIsTranscribing(true);
        setInterimTranscript('');
        setAudioLevel(0);
        const transcriptionController = new AbortController();
        const transcriptionTimeout = window.setTimeout(() => {
          transcriptionController.abort();
        }, 30000);

        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          if (audioBlob.size === 0) return;

          const formData = new FormData();
          const ext = mimeType.includes('mp4') ? 'm4a' : 'webm';
          formData.append('file', audioBlob, `recording.${ext}`);

          const token = localStorage.getItem('auth_token');
          const uploadStartedAt = performance.now();
          const response = await fetch(`${API}/tts/transcribe`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            signal: transcriptionController.signal,
          });
          if (response.ok) {
            const data = await response.json();
            console.log('[ConversationTiming] recorded_transcription_done', {
              uploadAndTranscribeMs: Math.round(performance.now() - uploadStartedAt),
              totalElapsedMs: Math.round(performance.now() - recordedStartedAt),
              chars: data.text?.trim()?.length || 0
            });
            if (data.text?.trim()) onResult(data.text.trim(), { recordedMode: autoStop });
          } else {
            throw new Error(`Transcription request failed: ${response.status}`);
          }
        } catch (err) {
          console.error('Whisper transcription failed:', err);
          if (err.name === 'AbortError') {
            notify('transcription-timeout', 'Transcription timed out. Please try again.');
          } else {
            notify('transcription-failed', 'Transcription failed, please try again');
          }
        } finally {
          window.clearTimeout(transcriptionTimeout);
          if (maxRecordingTimer) window.clearTimeout(maxRecordingTimer);
          setIsTranscribing(false);
          whisperStreamRef.current?.getTracks().forEach(t => t.stop());
          whisperStreamRef.current = null;
          try {
            whisperSourceRef.current?.disconnect();
          } catch {
            // Source may already be disconnected when recording stops.
          }
          whisperSourceRef.current = null;
          if (!isIOS()) {
            whisperAudioCtxRef.current?.close();
            whisperAudioCtxRef.current = null;
          }
          analyserRef.current = null;
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }
          mediaRecorderRef.current = null;
          audioChunksRef.current = [];
          setIsRecordedMode(false);
        }
      };

      mediaRecorder.start(1000);
      if (autoStop) {
        maxRecordingTimer = window.setTimeout(() => {
          if (mediaRecorderRef.current?.state === 'recording') {
            console.log('[ConversationVoice] Whisper max recording duration (90s) reached, auto-stopping');
            mediaRecorderRef.current.stop();
          }
        }, 90000);
      }
      setIsListening(true);
      return true;
    } catch (err) {
      console.error('Mic access failed:', err);
      notify('mic-access-denied', getMicErrorMessage(err));
      shouldRestartRef.current = false;
      setIsListening(false);
      setAudioLevel(0);
      setIsRecordedMode(false);
      whisperStreamRef.current?.getTracks().forEach(t => t.stop());
      whisperStreamRef.current = null;
      try {
        whisperSourceRef.current?.disconnect();
      } catch {
        // Source may already be disconnected after a failed start.
      }
      whisperSourceRef.current = null;
      if (!isIOS()) {
        whisperAudioCtxRef.current?.close();
        whisperAudioCtxRef.current = null;
      }
      return false;
    }
  };

  const stopWhisperListening = () => {
    shouldRestartRef.current = false;
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsListening(false);
    setAudioLevel(0);
  };

  const stopDeepgramListening = () => {
    shouldRestartRef.current = false;
    setIsRecordedMode(false);
    deepgramRunRef.current += 1;
    deepgramReconnectAttemptsRef.current = 0;
    try {
      deepgramRecorderRef.current?.stop();
    } catch {
      // Recorder may already be inactive during socket teardown.
    }
    deepgramRecorderRef.current = null;
    deepgramProcessorRef.current?.disconnect();
    deepgramProcessorRef.current = null;
    deepgramCaptureModeRef.current = 'none';
    if (deepgramWsRef.current) {
      deepgramWsRef.current.onclose = null;
      deepgramWsRef.current.close();
      deepgramWsRef.current = null;
    }
    if (deepgramStreamRef.current) {
      deepgramStreamRef.current.getTracks().forEach(t => t.stop());
      deepgramStreamRef.current = null;
    }
    // deepgramAudioCtxRef is intentionally left open here. Closing and re-creating it
    // on every turn would place the new AudioContext outside iOS's user-gesture window,
    // causing ScriptProcessor to send zero packets on turn 2+. It is closed on unmount.
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    analyserRef.current = null;
    pendingFinalTranscriptRef.current = '';
    setIsListening(false);
    setInterimTranscript('');
    setAudioLevel(0);
  };

  const startDeepgramListening = async (onResult, options = {}) => {
    const requestedAt = performance.now();
    const isReconnect = options.isReconnect === true;
    if (deepgramWsRef.current) {
      if (isReconnect) {
        deepgramWsRef.current.onclose = null;
        deepgramWsRef.current.onerror = null;
        deepgramWsRef.current.close();
        deepgramWsRef.current = null;
        try {
          deepgramRecorderRef.current?.stop();
        } catch {
          // Recorder may already be inactive during reconnect.
        }
        deepgramRecorderRef.current = null;
        deepgramProcessorRef.current?.disconnect();
        deepgramProcessorRef.current = null;
        deepgramCaptureModeRef.current = 'none';
        deepgramStreamRef.current?.getTracks().forEach(t => t.stop());
        deepgramStreamRef.current = null;
        // deepgramAudioCtxRef is intentionally kept alive on reconnect so the
        // already-unlocked context (created by primeAudio) can be reused.
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        analyserRef.current = null;
      } else {
        stopDeepgramListening();
      }
    }

    onResultCallbackRef.current = onResult;
    shouldRestartRef.current = true;
    setIsRecordedMode(false);
    deepgramCaptureModeRef.current = 'starting';
    const runId = isReconnect ? deepgramRunRef.current : deepgramRunRef.current + 1;
    deepgramRunRef.current = runId;

    const fallbackToRecordedTranscription = () => {
      console.warn('[ConversationTiming] deepgram_fallback_to_recorded', {
        elapsedMs: Math.round(performance.now() - requestedAt),
        captureMode: deepgramCaptureModeRef.current
      });
      const callback = onResultCallbackRef.current;
      stopDeepgramListening();
      if (callback) {
        return startWhisperListening(callback, { autoStop: options.whisperAutoStop === true });
      }
      return false;
    };

    const scheduleDeepgramReconnect = () => {
      if (!shouldRestartRef.current || deepgramRunRef.current !== runId) return;
      if (deepgramReconnectAttemptsRef.current >= DEEPGRAM_MAX_RECONNECTS) {
        fallbackToRecordedTranscription();
        return;
      }

      deepgramReconnectAttemptsRef.current += 1;
      setTimeout(() => {
        if (shouldRestartRef.current && deepgramRunRef.current === runId) {
          startDeepgramListening(onResultCallbackRef.current, { isReconnect: true });
        }
      }, 700);
    };

    try {
      // Request microphone access before any network await so iOS can show
      // the permission prompt directly from the user's tap.
      let stream = deepgramStreamRef.current?.active ? deepgramStreamRef.current : null;
      if (!stream) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: DEEPGRAM_AUDIO_CONSTRAINTS
          });
        } catch (micErr) {
          console.error('Mic access failed:', micErr);
          notify('mic-access-denied', getMicErrorMessage(micErr));
          shouldRestartRef.current = false;
          setIsListening(false);
          setAudioLevel(0);
          setIsRecordedMode(false);
          return false;
        }
      }
      deepgramStreamRef.current = stream;

      // Fetch a short-lived Deepgram token. The backend never returns the long-lived API key.
      let dgToken = deepgramTokenCacheRef.current.token;
      if (!dgToken || Date.now() > deepgramTokenCacheRef.current.expiresAt) {
        console.log('[ConversationTiming] deepgram_token_fetch_start');
        const authToken = localStorage.getItem('auth_token');
        const { data: tokenData } = await axios.get(`${API}/tts/token`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        dgToken = tokenData.token;
        const ttlMs = Math.max(1000, ((tokenData.expires_in || 60) - 5) * 1000);
        deepgramTokenCacheRef.current = { token: dgToken, expiresAt: Date.now() + ttlMs };
        console.log('[ConversationTiming] deepgram_token_fetch_done', {
          elapsedMs: Math.round(performance.now() - requestedAt)
        });
      } else {
        console.log('[ConversationTiming] deepgram_token_cache_hit', {
          elapsedMs: Math.round(performance.now() - requestedAt)
        });
      }

      // Audio level analyser — throttled to 10fps.
      // Reuse the AudioContext pre-created by primeAudio() if available. On iOS,
      // a new AudioContext created here (after getUserMedia + token fetch) would be
      // outside the user-gesture window, start suspended, and cause ScriptProcessor
      // to silently send zero packets — so primeAudio() creates it synchronously.
      const existingCtx = deepgramAudioCtxRef.current;
      const audioCtx = (existingCtx && existingCtx.state !== 'closed')
        ? existingCtx
        : new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx !== deepgramAudioCtxRef.current) {
        deepgramAudioCtxRef.current = audioCtx;
        console.log('[ConversationVoice] startDeepgram: created new AudioContext (no pre-primed ctx available)');
      } else {
        console.log('[ConversationVoice] startDeepgram: reusing pre-primed AudioContext, state:', audioCtx.state);
      }
      if (audioCtx.state === 'suspended') {
        console.warn('[ConversationVoice] startDeepgram: AudioContext suspended — attempting resume');
        await audioCtx.resume();
      }
      console.log('[ConversationVoice] startDeepgram: AudioContext state before connect:', audioCtx.state);
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let lastLevelUpdate = 0;
      const updateLevel = () => {
        if (!shouldRestartRef.current) return;
        const now = Date.now();
        if (now - lastLevelUpdate > 100) {
          analyser.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((s, v) => s + v, 0) / dataArray.length;
          setAudioLevel(Math.min(100, (avg / 255) * 150));
          lastLevelUpdate = now;
        }
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();

      // Open Deepgram WebSocket directly from browser (no Render proxy needed)
      const endpointingMs = getDeepgramEndpointingMs();
      const params = new URLSearchParams({
        model: 'nova-3',
        language: 'en-US',
        encoding: 'linear16',
        sample_rate: '16000',
        channels: '1',
        punctuate: 'true',
        smart_format: 'true',
        interim_results: 'true',
        endpointing: String(endpointingMs),
      });
      const ws = new WebSocket(
        `wss://api.deepgram.com/v1/listen?${params}`,
        ['token', (dgToken || '').trim()]
      );
      deepgramWsRef.current = ws;

      ws.onopen = () => {
        if (deepgramRunRef.current !== runId) {
          ws.close();
          return;
        }
        console.log('[ConversationTiming] deepgram_ws_open', {
          elapsedMs: Math.round(performance.now() - requestedAt),
          endpointingMs
        });

        // AudioContext must be running before connecting ScriptProcessor.
        // If it's still suspended here, onaudioprocess will never fire — detect early and fall back.
        if (audioCtx.state !== 'running') {
          console.warn('[ConversationVoice] ws.onopen: AudioContext is', audioCtx.state, '— falling back to recorded mode');
          ws.onclose = null;
          ws.close();
          fallbackToRecordedTranscription();
          return;
        }

        setIsListening(true);

        const downsampleTo16k = (input, inputSampleRate) => {
          if (!input.length) return null;
          const targetSampleRate = 16000;
          const ratio = inputSampleRate / targetSampleRate;
          const outputLength = Math.max(1, Math.floor(input.length / ratio));
          const output = new Int16Array(outputLength);

          for (let i = 0; i < outputLength; i++) {
            const start = Math.floor(i * ratio);
            const end = Math.min(input.length, Math.floor((i + 1) * ratio));
            let sum = 0;
            let count = 0;

            for (let j = start; j < end; j++) {
              sum += input[j];
              count += 1;
            }

            const sample = Math.max(-1, Math.min(1, count ? sum / count : input[start] || 0));
            output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
          }

          return output.buffer;
        };

        let audioPacketsSent = 0;
        const sendAudioBuffer = (inputBuffer) => {
          if (deepgramRunRef.current !== runId || ws.readyState !== WebSocket.OPEN) return;
          const pcm = downsampleTo16k(inputBuffer, audioCtx.sampleRate);
          if (pcm && pcm.byteLength > 0) {
            ws.send(pcm);
            audioPacketsSent++;
            if (audioPacketsSent === 1) {
              console.log('[ConversationVoice] first audio packet sent to Deepgram via', deepgramCaptureModeRef.current);
              console.log('[ConversationTiming] deepgram_first_audio_packet', { captureMode: deepgramCaptureModeRef.current });
            }
          }
        };

        const startScriptProcessorCapture = () => {
          const processor = audioCtx.createScriptProcessor(2048, 1, 1);
          deepgramCaptureModeRef.current = 'script_processor';
          deepgramProcessorRef.current = processor;
          processor.onaudioprocess = (event) => {
            sendAudioBuffer(event.inputBuffer.getChannelData(0));
          };
          source.connect(processor);
          processor.connect(audioCtx.destination);
          console.log('[ConversationVoice] Deepgram capture using ScriptProcessor fallback');
        };

        const startAudioWorkletCapture = async () => {
          if (!audioCtx.audioWorklet || typeof AudioWorkletNode === 'undefined') {
            throw new Error('AudioWorklet is not supported');
          }
          if (!deepgramWorkletLoadedRef.current) {
            await audioCtx.audioWorklet.addModule('/deepgram-worklet.js');
            deepgramWorkletLoadedRef.current = true;
          }
          if (deepgramRunRef.current !== runId || !shouldRestartRef.current || ws.readyState !== WebSocket.OPEN) {
            return;
          }
          const workletNode = new AudioWorkletNode(audioCtx, 'deepgram-pcm-processor');
          const silentGain = audioCtx.createGain();
          silentGain.gain.value = 0;
          deepgramCaptureModeRef.current = 'audio_worklet';
          workletNode.port.onmessage = (message) => {
            sendAudioBuffer(message.data);
          };
          source.connect(workletNode);
          workletNode.connect(silentGain);
          silentGain.connect(audioCtx.destination);
          deepgramProcessorRef.current = {
            disconnect: () => {
              workletNode.port.onmessage = null;
              try { workletNode.disconnect(); } catch {}
              try { silentGain.disconnect(); } catch {}
            }
          };
          console.log('[ConversationVoice] Deepgram capture using AudioWorklet');
        };

        startAudioWorkletCapture().catch((error) => {
          console.warn('[ConversationVoice] AudioWorklet capture unavailable, using ScriptProcessor:', error?.message || error);
          if (deepgramRunRef.current !== runId || !shouldRestartRef.current || ws.readyState !== WebSocket.OPEN) return;
          startScriptProcessorCapture();
        });

        // Safety net: if no audio has been sent after 2.5 s, the capture path is broken
        // (e.g. AudioContext suspended, ScriptProcessor silent, worklet stalled).
        // Fall back to recorded mode rather than silently dropping the user's speech.
        setTimeout(() => {
          if (deepgramRunRef.current === runId && shouldRestartRef.current && audioPacketsSent === 0) {
            console.warn('[ConversationVoice] No audio packets sent after 2.5s — falling back to recorded mode');
            ws.onclose = null;
            ws.close();
            fallbackToRecordedTranscription();
          }
        }, 2500);
      };

      ws.onmessage = (event) => {
        if (deepgramRunRef.current !== runId) return;
        try {
          const msg = JSON.parse(event.data);
          if (msg.type !== 'Results') return;
          deepgramReconnectAttemptsRef.current = 0;
          const transcript = msg.channel?.alternatives?.[0]?.transcript || '';

          if (msg.speech_final) {
            console.log('[ConversationTiming] deepgram_speech_final', { chars: transcript.trim().length });
            // End of speech — commit the full accumulated text
            const full = pendingFinalTranscriptRef.current
              ? `${pendingFinalTranscriptRef.current} ${transcript}`.trim()
              : transcript.trim();
            pendingFinalTranscriptRef.current = '';
            setInterimTranscript('');
            if (full && onResultCallbackRef.current) {
              onResultCallbackRef.current(full);
            }
          } else if (msg.is_final) {
            // Segment finalized but utterance continues — accumulate silently
            if (transcript) {
              pendingFinalTranscriptRef.current = pendingFinalTranscriptRef.current
                ? `${pendingFinalTranscriptRef.current} ${transcript}`.trim()
                : transcript.trim();
              setInterimTranscript(pendingFinalTranscriptRef.current);
            }
          } else {
            // Interim — show accumulated finals + current partial
            const display = pendingFinalTranscriptRef.current
              ? `${pendingFinalTranscriptRef.current} ${transcript}`.trim()
              : transcript;
            setInterimTranscript(display);
          }
        } catch {
          // Ignore malformed Deepgram messages.
        }
      };

      ws.onclose = () => {
        if (shouldRestartRef.current && deepgramRunRef.current === runId) {
          setIsListening(false);
          scheduleDeepgramReconnect();
        }
      };

      ws.onerror = () => {
        if (shouldRestartRef.current && deepgramRunRef.current === runId) {
          ws.close();
        }
      };

      return true;

    } catch (err) {
      console.error('Deepgram start failed:', err);
      if (isReconnect) {
        scheduleDeepgramReconnect();
        return false;
      } else {
        return fallbackToRecordedTranscription();
      }
    }
  };

  // Call this synchronously inside a user-gesture handler (e.g. a button onClick)
  // BEFORE any await, so iOS accepts the silent audio.play() that unlocks the element.
  // Also pre-creates deepgramAudioCtxRef so startDeepgramListening can reuse it later
  // (iOS suspends any AudioContext created after async work like getUserMedia + token fetch).
  const primeAudio = () => {
    console.log('[ConversationVoice] primeAudio: called');
    const unlocked = unlockAudio();
    console.log('[ConversationVoice] primeAudio: audioContextRef state after unlock:', audioContextRef.current?.state ?? 'none');

    // Pre-create Deepgram's AudioContext while still in the synchronous user-gesture
    // stack. On iOS, an AudioContext created after any await is placed outside the
    // gesture window and starts suspended — ScriptProcessor.onaudioprocess then
    // silently sends zero packets and the session falls back to Whisper.
    if (!deepgramAudioCtxRef.current || deepgramAudioCtxRef.current.state === 'closed') {
      try {
        const dgCtx = new (window.AudioContext || window.webkitAudioContext)();
        deepgramAudioCtxRef.current = dgCtx;
        console.log('[ConversationVoice] primeAudio: created deepgramAudioCtx, state:', dgCtx.state);
        if (dgCtx.state === 'suspended') {
          dgCtx.resume(); // synchronous intent — iOS honours this inside gesture
          console.log('[ConversationVoice] primeAudio: called resume() on deepgramAudioCtx');
        }
      } catch (e) {
        console.warn('[ConversationVoice] primeAudio: could not create deepgramAudioCtx', e);
      }
    } else {
      console.log('[ConversationVoice] primeAudio: deepgramAudioCtx already exists, state:', deepgramAudioCtxRef.current.state);
    }

    if (!whisperAudioCtxRef.current || whisperAudioCtxRef.current.state === 'closed') {
      try {
        const whisperCtx = new (window.AudioContext || window.webkitAudioContext)();
        whisperAudioCtxRef.current = whisperCtx;
        console.log('[ConversationVoice] primeAudio: created whisperAudioCtx, state:', whisperCtx.state);
        if (whisperCtx.state === 'suspended') {
          whisperCtx.resume();
          console.log('[ConversationVoice] primeAudio: called resume() on whisperAudioCtx');
        }
      } catch (e) {
        console.warn('[ConversationVoice] primeAudio: could not create whisperAudioCtx', e);
      }
    } else {
      console.log('[ConversationVoice] primeAudio: whisperAudioCtx already exists, state:', whisperAudioCtxRef.current.state);
    }

    return unlocked;
  };

  return {
    isListening,
    isSpeaking,
    isLoading,
    isSupported,
    selectedVoice,
    setSelectedVoice,
    playbackSpeed,
    setPlaybackSpeed,
    interimTranscript,
    audioLevel,
    isTranscribing,
    isRecordedMode,
    startWhisperListening,
    stopWhisperListening,
    prepareVoiceInput: requestDeepgramMicrophoneAccess,
    primeAudio,
    startDeepgramListening,
    stopDeepgramListening,
    speak,
    stopSpeaking,
    voiceOptions: SETTINGS_VOICE_OPTIONS
  };
}

/**
 * Voice Controls Component with Voice Selection
 */
export function VoiceControls({ content, onVoiceInput, className = '', showVoiceSelector = false }) {
  const {
    isListening,
    isSpeaking,
    isLoading,
    isTranscribing,
    isSupported,
    selectedVoice,
    setSelectedVoice,
    startWhisperListening,
    stopWhisperListening,
    speak,
    stopSpeaking,
    voiceOptions
  } = useVoiceFeatures();

  const [showVoiceMenu, setShowVoiceMenu] = useState(false);

  const handleVoiceInput = () => {
    if (isListening) {
      stopWhisperListening();
    } else {
      startWhisperListening((transcript) => {
        onVoiceInput(transcript);
      });
    }
  };

  const handleReadAloud = () => {
    if (isSpeaking || isLoading) {
      stopSpeaking();
    } else if (content && content.trim().length > 0) {
      // Extra safety check before calling speak
      speak(content);
    } else {
      toast.error('No text available to read');
    }
  };

  if (!isSupported && !content) {
    return null;
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* Voice to Text Button */}
      {onVoiceInput && isSupported && (
        <button
          onClick={handleVoiceInput}
          data-testid="voice-to-text-button"
          className={`p-2 rounded-lg transition-all ${
            isListening
              ? 'bg-red-500 text-white animate-pulse'
              : 'bg-[#E8E0D4] text-[#2A2420] hover:bg-[#D4C4A8]'
          }`}
          title={isListening ? 'Stop listening' : 'Start voice input'}
        >
          {isListening ? (
            <MicOff className="w-5 h-5" />
          ) : (
            <Mic className="w-5 h-5" />
          )}
        </button>
      )}

      {/* Text to Voice Button */}
      {content && (
        <button
          onClick={handleReadAloud}
          disabled={!content || content.trim().length === 0}
          data-testid="text-to-voice-button"
          className={`p-2 rounded-lg transition-all ${
            isSpeaking
              ? 'bg-purple-500 text-white animate-pulse'
              : isLoading
              ? 'bg-amber-500 text-white animate-pulse'
              : content && content.trim().length > 0
              ? 'bg-[#E8E0D4] text-[#2A2420] hover:bg-[#D4C4A8]'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          title={isSpeaking ? 'Stop reading' : isLoading ? 'Preparing...' : `Read with ${voiceOptions[selectedVoice]?.name}`}
        >
          {isSpeaking || isLoading ? (
            <VolumeX className="w-5 h-5" />
          ) : (
            <Volume2 className="w-5 h-5" />
          )}
        </button>
      )}

      {/* Voice Selection Button */}
      {showVoiceSelector && content && (
        <div className="relative">
          <button
            onClick={() => setShowVoiceMenu(!showVoiceMenu)}
            data-testid="voice-selector-button"
            className="p-2 rounded-lg bg-[#E8E0D4] text-[#2A2420] hover:bg-[#D4C4A8] transition-all"
            title="Select voice"
          >
            <Settings2 className="w-5 h-5" />
          </button>

          {showVoiceMenu && (
            <div className="absolute bottom-full right-0 mb-2 w-56 bg-white dark:bg-[#22201C] rounded-xl shadow-lg border border-[#E0D8CC] dark:border-[#2A2520] p-2 z-50">
              <p className="text-xs font-medium text-[#2A2420]/60 dark:text-gray-400 px-2 mb-2">
                Choose a voice
              </p>
              {Object.entries(voiceOptions).map(([key, voice]) => (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedVoice(key);
                    setShowVoiceMenu(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                    selectedVoice === key
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                      : 'hover:bg-[#F8F5F0] dark:hover:bg-[#2A2520]/50 text-[#2A2420] dark:text-[#F0EBE4]'
                  }`}
                >
                  <span className="font-medium">{voice.name}</span>
                  <span className="text-xs opacity-60 ml-1">- {voice.desc}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isListening && !isTranscribing && (
        <span className="text-sm text-red-600 font-medium animate-pulse">
          Recording — tap to stop
        </span>
      )}

      {isTranscribing && (
        <span className="text-sm text-stone-500 font-medium animate-pulse">
          Transcribing...
        </span>
      )}

      {isLoading && (
        <span className="text-sm text-amber-600 font-medium animate-pulse">
          Preparing...
        </span>
      )}
      
      {isSpeaking && (
        <span className="text-sm text-purple-600 font-medium animate-pulse">
          Reading...
        </span>
      )}
    </div>
  );
}

export default VoiceControls;
